<script>
//Repositories
import Repositories from '@/repositories';
const OrdersRepository = Repositories.get('orders');

//Mixins
import textMixin from '@/mixins/textMixin';
import dateMixin from '@/mixins/dateMixin';
import statusMixin from '@/mixins/statusMixin';

//Components
import GridComponent from '@/components/widgets/grids/api-linked-grid';
import StatusComponent from '@/components/widgets/badges/status';
import DeleteButton from '@/components/widgets/buttons/delete-button';

//Modals
import ModalLayout from '@/router/layouts/modal-view.vue';
import OrderInfoModal from '../../management/main.vue';
import ToOrderModal from '../../shared/modals/to-order.vue';
import ConfirmOrderModal from '../../shared/modals/confirm-order.vue';

export default {
  props: {
    filters: Object,
    fields: Array,
  },
  components: {
    GridComponent,
    StatusComponent,
    DeleteButton,
    ModalLayout,
    ToOrderModal,
    ConfirmOrderModal,
    OrderInfoModal,
  },
  mixins: [textMixin, dateMixin, statusMixin],
  data() {
    return {
      ordersList: [],
      fullListSize: 0,
      gridContext: null,
      isLoading: false,
      orderForModal: null,
      lastClickedOrder: null,
      errorIdPublic: [],
    };
  },
  created() {
    this.gridContext = {
      currentPage: 1,
      perPage: this.itemsPerPage,
      sortBy: 'statusOrder',
      sortDesc: false,
    };
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      OrdersRepository.getAll({ ...this.filters, ...this.gridContext })
        .then(({ data }) => {
          if (data != null && data.data != null) {
            this.ordersList = data.data;
            this.fullListSize = data.count;
          }
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => (this.isLoading = false));
    },
    orderUpdated(order) {
      let foundIndex = this.ordersList.findIndex(
        (x) => x.idPublic == order.idPublic
      );

      this.ordersList = [
        ...this.ordersList.slice(0, foundIndex),
        order,
        ...this.ordersList.slice(foundIndex + 1),
      ];

      this.orderForModal = order;
    },
    deleteOrder(idPublic) {
      OrdersRepository.delete(idPublic)
        .then(() => {
          let foundIndex = this.ordersList.findIndex(
            (x) => x.idPublic == idPublic
          );
          this.ordersList.splice(foundIndex, 1);

          this.loadData();
        })
        .catch(() => {
          this.errorIdPublic.push(idPublic);
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    contextChanged(context) {
      this.gridContext = context;
      this.loadData();
    },
    resetError(idPublic) {
      this.errorIdPublic = this.errorIdPublic.filter((x) => x != idPublic);
    },
    openModal(order, modalName) {
      this.orderForModal = order;
      this.lastClickedOrder = modalName + '-' + order.idPublic;
      this.$bvModal.show(modalName);
    },
    modalClosed() {
      this.orderForModal = null;
      this.loadData();
    },
  },
  computed: {
    ordersListComputed() {
      return this.ordersList.map((x) => {
        let status = this.getStatusFromCode(x.status, 'Orders');
        return { ...x, ...status };
      });
    },
    itemsPerPage() {
      return this.$store.getters['localstorage/getDynamicConfigs'](
        'gridConfig-orders-grid'
      ).itemsPerPage;
    },
  },
  watch: {
    filters() {
      this.ordersList = [];
      this.gridContext.currentPage = 1;
      this.loadData();
    },
  },
};
</script>

<template>
  <b-card no-body class="p-3 custom-inventory-shadow">
    <GridComponent
      v-if="gridContext != null"
      id="orders-grid"
      :fields="fields"
      :items="ordersListComputed"
      :is-busy="isLoading"
      :nbItems="fullListSize"
      :default-sort="gridContext.sortBy"
      :current-page="gridContext.currentPage"
      @context-changed="contextChanged"
    >
      <template #cell(number)="data">
        <span
          v-html="
            highlightMatches(
              data.item.number,
              filters != null ? filters.search : ''
            )
          "
        ></span>
      </template>

      <template #cell(externalNumber)="data">
        <span
          v-html="
            highlightMatches(
              data.item.externalNumber,
              filters != null ? filters.search : ''
            )
          "
        ></span>
      </template>

      <template #cell(statusOrder)="data">
        <div>
          <StatusComponent
            :text="data.item.statusName"
            :variant="data.item.statusVariant"
          />
        </div>
      </template>

      <template #cell(receptionDate)="data">
        {{
          data.item.receptionDate != null
            ? formatDate(data.item.receptionDate)
            : ''
        }}
      </template>
      <template #cell(creationDate)="data">
        {{ formatDate(data.item.creationDate) }}
      </template>

      <template #cell(manage)="data">
        <div @click.capture.prevent="openModal(data.item, 'order-info-modal')">
          <b-link
            :to="{
              name: 'manageOrder',
              params: { idPublic: data.item.idPublic },
            }"
          >
            <i
              v-if="
                lastClickedOrder === 'order-info-modal-' + data.item.idPublic
              "
              class="fas fa-eye pl-2 pr-2"
              style="cursor: pointer; font-size: 1.3em; color: #20976e"
            ></i>
            <i
              v-else
              class="fas fa-eye text-success pl-2 pr-2"
              style="cursor: pointer; font-size: 1.3em"
            ></i>
          </b-link>
        </div>
      </template>

      <template #cell(order)="data">
        <i
          v-if="data.item.isClosed"
          class="fas fa-check text-light pl-2 pr-2"
        ></i>
        <i
          v-else-if="data.item.statusCode == 'ORDERED'"
          class="fas fa-check text-secondary pl-2 pr-2"
        ></i>
        <i
          v-else-if="data.item.statusCode == 'TO_ORDER'"
          class="far fa-arrow-alt-circle-right text-success pl-2 pr-2"
          @click="openModal(data.item, 'to-order-in-grid-modal')"
          style="cursor: pointer; font-size: 1.3em"
        ></i>
        <i
          v-else-if="data.item.statusCode == 'CONFIRMED'"
          class="far fa-arrow-alt-circle-right text-success pl-2 pr-2"
          @click="openModal(data.item, 'confirm-order-in-grid-modal')"
          style="cursor: pointer; font-size: 1.3em"
        ></i>
        <i v-else class="fas fa-hourglass-half text-warning pl-2 pr-2"></i>
      </template>

      <template #cell(delete)="data">
        <DeleteButton
          :key="data.item.idPublic"
          @clicked="deleteOrder(data.item.idPublic)"
          :error="errorIdPublic.includes(data.item.idPublic)"
          @reset="resetError(data.item.idPublic)"
        />
      </template>
    </GridComponent>

    <ModalLayout
      name="order-info-modal"
      @modal-closed="modalClosed"
      :title="
        $t('orders.order') +
          ' ' +
          (orderForModal != null ? orderForModal.number : '')
      "
      :route="{
        name: 'manageOrder',
        params: {
          idPublic: orderForModal != null ? orderForModal.idPublic : '',
        },
      }"
    >
      <div v-if="orderForModal != null">
        <OrderInfoModal
          :orderIdPublic="orderForModal.idPublic"
          @order-updated="orderUpdated"
        />
      </div>
    </ModalLayout>
    <ToOrderModal
      :order="orderForModal"
      modalId="to-order-in-grid-modal"
      @order-updated="orderUpdated"
      @modal-closed="modalClosed"
    />
    <ConfirmOrderModal
      :order="orderForModal"
      modalId="confirm-order-in-grid-modal"
      @order-updated="orderUpdated"
      @modal-closed="modalClosed"
    />
  </b-card>
</template>

<style scoped>
a,
a:hover,
a:focus,
a:active {
  text-decoration: none;
  color: inherit;
}
</style>
